import React, { useState } from "react";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import { InsightBox } from "../../../ForBrands/ui/BrandUI";
import { LegendRow } from "../../../ForBrands/ui/LegendItem";
import { CHART } from "../../../ForBrands/ui/charts/chartTokens";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { PlayGroupSwitch, ProfileInsight, StatTile, StatsProvenance } from "../profileSections";
import { MobileStatCell, RankLegend } from "../mobile/mobileProfileSections";

// The Performance section's first card (spec B3 a): where the stats come
// from (StatsProvenance: the stale notice and the "Stats from" line), then
// the season box score's nine tiles with their State and Peer group rank
// chips (Kobi's 10/7 rank labels, "Top 3% in <group>"), as the profile
// showed them, without the shooting rows and the radar the new cards
// replace. The rank legend is the section's legend row (the profile's
// LegendBadge look, as a named list). `showSummary` keeps the profile's
// own season summary when the payload has no read of its own.
//
// School and grassroots (Kobi's 10/21 note, D7): with two or more play
// groups (profileModel.playGroups) a School | Grassroots switch (College,
// Pro when the athlete played there) sits under the header, and the
// subtitle names the line shown. The group the peer group ranks shows its
// ranked line with the rank legend; another group its season total, with no
// ranks and no legend. With one group there is no switch and the subtitle
// names the group. The "Stats from" line counts games per source as before.

// The profile's rank blue (RankBadge) for a state rank, its grey for the peer group.
const RANK_BLUE = "#2196f3";

const SeasonBoxScoreCard = ({ model, narratives, showSummary = false, cardProps = {} }) => {
  const phone = usePhoneTheme();
  const [picked, setPicked] = useState(null);
  const { self } = narratives;
  const line = typeof narratives.boxLine === "function" ? narratives.boxLine(picked) : model;
  const subtitle = typeof narratives.boxScoreSubtitleFor === "function" ? narratives.boxScoreSubtitleFor(line) : narratives.boxScoreSubtitle;
  const performanceSummary =
    typeof narratives.performanceSummaryFor === "function" ? narratives.performanceSummaryFor(line) : narratives.performanceSummary;
  const empty = !line.hasBoxScore;
  const emptyNote = self
    ? "No season stats on file yet. Stats sync from your program's data partner once your coach verifies you, or add them under Edit my profile."
    : "No season stats on file yet. Stats appear once they sync from the athlete's program data partner.";
  const summaryLabel = self ? "Your season summary" : "Season summary";
  return (
    <CollapsibleCard title="Season box score" subtitle={subtitle} {...cardProps}>
      <PlayGroupSwitch groups={model.playGroups} value={line.key} onChange={setPicked} compact={phone} />
      <StatsProvenance model={model} compact={phone} />
      {phone ? (
        <div className="grid grid-cols-2 gap-[10px]">
          {line.boxScore.map((s) => (
            <MobileStatCell key={s.label} value={empty ? null : s.value} label={s.label} chips={empty ? undefined : s.chips} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-[10px] md:grid-cols-5 xl:grid-cols-9">
          {line.boxScore.map((s) => (
            <StatTile key={s.label} value={empty ? null : s.value} label={s.label} chips={empty ? [] : s.chips} />
          ))}
        </div>
      )}
      {empty && <p className="bm-body-m text-bm-muted">{emptyNote}</p>}
      {!empty && phone && <RankLegend items={line.boxScore} />}
      {!empty && !phone && line.hasPercentiles && (
        // The ranks beside the numbers: the same-state subset (blue, when one shows) and the whole peer group (grey).
        <LegendRow
          label="Rank legend"
          items={[
            line.hasStateRanks && { key: "state", swatch: "dot", color: RANK_BLUE, label: "State" },
            { key: "peer", swatch: "dot", color: CHART.muted, label: "Peer group" },
          ]}
        />
      )}
      {showSummary && !empty && performanceSummary && (
        phone ? (
          <InsightBox compact title={summaryLabel}>
            {performanceSummary}
          </InsightBox>
        ) : (
          <ProfileInsight label={summaryLabel}>{performanceSummary}</ProfileInsight>
        )
      )}
    </CollapsibleCard>
  );
};

export default SeasonBoxScoreCard;
